/* global BigInt */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAddress } from "viem";
import { usePublicClient, useWriteContract } from "wagmi";
import BOB_PRESALE_V2_ABI from "../contracts/bobPresaleV2Abi";

const AUTH_BASE = (process.env.REACT_APP_PRESALE_AUTH_BASE || "").trim();
const AUTH_TIMEOUT_MS = 8000;
const POLL_MS = 8000;

function prettyErr(e) {
  return String(e?.shortMessage || e?.details || e?.message || e || "Transaction failed");
}

function statusFromState(stateVal, count, max) {
  const s = Number(stateVal || 0);
  const reachedCap = Number(count || 0) >= Number(max || 0) && Number(max || 0) > 0;
  if (reachedCap) return { key: "ended", label: "Ended" };
  if (s === 0) return { key: "upcoming", label: "Upcoming" };
  if (s === 1) return { key: "live", label: "Live" };
  return { key: "ended", label: "Ended" };
}

export default function usePresaleV2(presaleAddress, connectedAddress, expectedChainId) {
  const chainId = Number(expectedChainId || 143);
  const publicClient = usePublicClient({ chainId });
  const { writeContractAsync } = useWriteContract();

  const presaleReady = Boolean(isAddress(presaleAddress));
  const hasUser = Boolean(isAddress(connectedAddress));

  const [uiError, setUiError] = useState("");
  const [txHash, setTxHash] = useState("");
  const [isWriting, setIsWriting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const [presaleState, setPresaleState] = useState(0);
  const [roundId, setRoundId] = useState(0n);
  const [contributionWeiPerWallet, setContributionWeiPerWallet] = useState(0n);
  const [maxParticipants, setMaxParticipants] = useState(0n);
  const [participantCount, setParticipantCount] = useState(0n);
  const [totalRaisedWei, setTotalRaisedWei] = useState(0n);
  const [developerWallet, setDeveloperWallet] = useState("");
  const [owner, setOwner] = useState("");
  const [userContributedWei, setUserContributedWei] = useState(0n);
  const [hasParticipated, setHasParticipated] = useState(false);

  const [authDeadline, setAuthDeadline] = useState("");
  const [authSig, setAuthSig] = useState("");
  const [authStatus, setAuthStatus] = useState({ state: "idle", msg: "" });

  const [refreshNonce, setRefreshNonce] = useState(0);
  const authReqRef = useRef(0);
  const authAbortRef = useRef(null);
  const authIdentityRef = useRef("");

  const forceRefresh = useCallback(() => {
    setRefreshNonce((x) => x + 1);
  }, []);

  const readAll = useCallback(async () => {
    if (!presaleReady || !publicClient) return;
    const chain = { address: presaleAddress, abi: BOB_PRESALE_V2_ABI };
    try {
      const [rState, rRoundId, rContribution, rMax, rCount, rRaised, rDev, rOwner] = await Promise.all([
        publicClient.readContract({ ...chain, functionName: "presaleState" }),
        publicClient.readContract({ ...chain, functionName: "roundId" }),
        publicClient.readContract({ ...chain, functionName: "contributionWeiPerWallet" }),
        publicClient.readContract({ ...chain, functionName: "maxParticipants" }),
        publicClient.readContract({ ...chain, functionName: "participantCount" }),
        publicClient.readContract({ ...chain, functionName: "totalRaisedWei" }),
        publicClient.readContract({ ...chain, functionName: "developerWallet" }),
        publicClient.readContract({ ...chain, functionName: "owner" }),
      ]);

      setPresaleState(Number(rState || 0));
      setRoundId(BigInt(rRoundId || 0));
      setContributionWeiPerWallet(BigInt(rContribution || 0));
      setMaxParticipants(BigInt(rMax || 0));
      setParticipantCount(BigInt(rCount || 0));
      setTotalRaisedWei(BigInt(rRaised || 0));
      setDeveloperWallet(String(rDev || ""));
      setOwner(String(rOwner || ""));

      if (hasUser) {
        const [rHas, rContrib] = await Promise.all([
          publicClient.readContract({ ...chain, functionName: "hasParticipated", args: [connectedAddress] }),
          publicClient.readContract({ ...chain, functionName: "contributedWei", args: [connectedAddress] }),
        ]);
        setHasParticipated(Boolean(rHas));
        setUserContributedWei(BigInt(rContrib || 0));
      } else {
        setHasParticipated(false);
        setUserContributedWei(0n);
      }
    } catch (e) {
      // Keep public UI clean on transient read issues; writes still surface errors.
      // eslint-disable-next-line no-console
      console.warn("[PresaleV2 readAll]", prettyErr(e));
    }
  }, [presaleReady, publicClient, presaleAddress, hasUser, connectedAddress]);

  useEffect(() => {
    readAll();
  }, [readAll, refreshNonce]);

  useEffect(() => {
    if (!presaleReady) return;
    let alive = true;
    const id = setInterval(() => {
      if (!alive) return;
      readAll();
    }, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [presaleReady, readAll]);

  // Keep UI in sync when other wallets participate: refresh on each new block.
  useEffect(() => {
    if (!presaleReady || !publicClient) return;
    let alive = true;
    let unwatch;
    try {
      unwatch = publicClient.watchBlockNumber({
        emitOnBegin: false,
        poll: true,
        onBlockNumber: () => {
          if (!alive) return;
          readAll();
        },
        onError: () => {},
      });
    } catch {
      unwatch = undefined;
    }
    return () => {
      alive = false;
      try {
        if (typeof unwatch === "function") unwatch();
      } catch {}
    };
  }, [presaleReady, publicClient, readAll]);

  const fetchAuth = useCallback(
    async () => {
      if (!hasUser) return;
      const reqId = ++authReqRef.current;
      setAuthStatus({ state: "fetching", msg: "Checking eligibility..." });
      const url = AUTH_BASE ? AUTH_BASE.replace(/\/+$/, "") + "/auth" : "/auth";
      try {
        if (authAbortRef.current) authAbortRef.current.abort();
      } catch {}
      const controller = new AbortController();
      authAbortRef.current = controller;
      const timeoutId = setTimeout(() => {
        try {
          controller.abort();
        } catch {}
      }, AUTH_TIMEOUT_MS);
      try {
        const resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            address: connectedAddress,
            buyer: connectedAddress,
            chainId,
          }),
          signal: controller.signal,
        });
        const data = await resp.json().catch(() => ({}));
        if (reqId !== authReqRef.current) return;

        const denied = !resp.ok || data?.ok === false || data?.eligible === false || data?.authorized === false;
        if (denied) {
          setAuthStatus({ state: "denied", msg: String(data?.error || "Not eligible") });
          setAuthDeadline("");
          setAuthSig("");
          return;
        }

        const d = Number(data?.deadline || data?.authDeadline || data?.deadlineSec || 0);
        const sig = String(data?.signature || data?.authSig || data?.sig || "");
        setAuthDeadline(d > 0 ? String(d) : "");
        setAuthSig(sig && sig.startsWith("0x") ? sig : "");
        setAuthStatus({ state: "ok", msg: "Authorized." });
      } catch (e) {
        if (reqId !== authReqRef.current) return;
        const aborted = String(e?.name || "").toLowerCase().includes("abort");
        if (aborted) {
          setAuthStatus({ state: "error", msg: "Could not verify eligibility. Try reconnecting." });
        } else {
          setAuthStatus({ state: "error", msg: prettyErr(e) });
        }
        setAuthDeadline("");
        setAuthSig("");
      } finally {
        clearTimeout(timeoutId);
        if (authAbortRef.current === controller) {
          authAbortRef.current = null;
        }
      }
    },
    [hasUser, connectedAddress, chainId]
  );

  useEffect(() => {
    const key = [String(presaleAddress || "").toLowerCase(), String(connectedAddress || "").toLowerCase(), String(chainId || 0)].join("|");
    if (authIdentityRef.current === key) return;
    authIdentityRef.current = key;

    authReqRef.current += 1;
    try {
      if (authAbortRef.current) authAbortRef.current.abort();
    } catch {}
    authAbortRef.current = null;

    if (!hasUser) {
      setAuthStatus({ state: "idle", msg: "" });
      setAuthDeadline("");
      setAuthSig("");
    } else {
      setAuthStatus({ state: "fetching", msg: "Checking eligibility..." });
      setAuthDeadline("");
      setAuthSig("");
    }
  }, [presaleAddress, connectedAddress, chainId, hasUser]);

  useEffect(() => {
    if (!hasUser) {
      setAuthStatus({ state: "idle", msg: "" });
      setAuthDeadline("");
      setAuthSig("");
      return;
    }
    fetchAuth();
  }, [hasUser, connectedAddress, fetchAuth]);

  const writeTx = useCallback(
    async (functionName, args = [], value) => {
      if (!presaleReady || !publicClient) throw new Error("Presale is not configured.");
      setUiError("");
      setIsWriting(true);
      try {
        const hash = await writeContractAsync({
          address: presaleAddress,
          abi: BOB_PRESALE_V2_ABI,
          functionName,
          args,
          ...(value != null ? { value } : {}),
        });
        setTxHash(hash);
        setIsConfirming(true);
        await publicClient.waitForTransactionReceipt({ hash, confirmations: 1 });
        await readAll();
        return hash;
      } catch (e) {
        const msg = prettyErr(e);
        setUiError(msg);
        throw e;
      } finally {
        setIsWriting(false);
        setIsConfirming(false);
      }
    },
    [presaleReady, publicClient, writeContractAsync, presaleAddress, readAll]
  );

  const buy = useCallback(async () => {
    if (statusFromState(presaleState, participantCount, maxParticipants).key !== "live") {
      throw new Error("Presale is not live.");
    }
    const denied = authStatus.state === "denied";
    if (denied) throw new Error(authStatus.msg || "This wallet is not eligible for the presale.");
    if (authStatus.state === "error") throw new Error("Could not verify eligibility. Try reconnecting.");
    if (!contributionWeiPerWallet || contributionWeiPerWallet <= 0n) throw new Error("Invalid contribution amount.");
    return writeTx("buy", [], contributionWeiPerWallet);
  }, [presaleState, participantCount, maxParticipants, authStatus.state, authStatus.msg, contributionWeiPerWallet, writeTx]);

  const exportBuyersPaged = useCallback(
    async (start = 0, size = 200) => {
      if (!presaleReady || !publicClient) return [];
      const out = await publicClient.readContract({
        address: presaleAddress,
        abi: BOB_PRESALE_V2_ABI,
        functionName: "participantsPaged",
        args: [BigInt(start), BigInt(size)],
      });
      return Array.isArray(out) ? out : [];
    },
    [presaleReady, publicClient, presaleAddress]
  );

  const exportAllBuyers = useCallback(async () => {
    const total = Number(participantCount || 0n);
    const page = 200;
    const all = [];
    for (let i = 0; i < total; i += page) {
      const batch = await exportBuyersPaged(i, Math.min(page, total - i));
      all.push(...batch);
    }
    return { buyers: all, total: all.length };
  }, [participantCount, exportBuyersPaged]);

  const status = useMemo(
    () => statusFromState(presaleState, participantCount, maxParticipants),
    [presaleState, participantCount, maxParticipants]
  );

  const unsupported = useCallback(async () => {
    throw new Error("Not supported on BOBPresaleV2.");
  }, []);

  return {
    chainId,
    expectedChainId: chainId,
    presaleReady,
    status,
    owner,
    ownerAddress: owner,
    contractOwner: owner,
    participantCount: Number(participantCount || 0n),
    contributionWeiPerWallet,
    totalRaisedWei,
    maxParticipants,
    developerWallet,
    presaleState,
    roundId,
    userContributedWei,
    userContributionWei: userContributedWei,
    hasParticipated,
    amountMon: "",
    setAmountMon: () => {},
    authStatus,
    authDeadline,
    authSig,
    fetchAuth,
    isWriting,
    isConfirming,
    uiError,
    txHash,
    buy,
    forceRefresh,
    exportAllBuyers,
    exportBuyersPaged,
    devSetPhase: (stateVal) => writeTx("setPresaleState", [Number(stateVal)]),
    setContributionWeiPerWallet: (wei) => writeTx("setContributionWeiPerWallet", [BigInt(wei)]),
    setMaxParticipants: (n) => writeTx("setMaxParticipants", [BigInt(n)]),
    resetPresale: (wei, max, stateVal) => writeTx("resetPresale", [BigInt(wei), BigInt(max), Number(stateVal)]),
    addToAllowlist: (arr) => writeTx("addToAllowlist", [arr]),
    removeFromAllowlist: (arr) => writeTx("removeFromAllowlist", [arr]),
    devMode: false,
    devAllowlistBypass: false,
    manualPhaseSet: false,
    manualPhaseValue: 0,
    signer: "",
    refundCursor: 0n,
    claimsEnabled: false,
    claimToken: "",
    walletCapBps: 0,
    presaleAllocationBps: 0,
    outstandingContributionsWei: 0n,
    totalForceToTreasuryWei: 0n,
    setTimes: unsupported,
    devSetAllowlistBypass: unsupported,
    setCapBps: unsupported,
    setTokensPerMon: unsupported,
    setSoftCapMon: unsupported,
    setClaimToken: unsupported,
    setClaimsEnabled: unsupported,
    devStartNow: unsupported,
    devEndNow: unsupported,
    devRefundOutstandingCursor: unsupported,
    devRefundAndResetCursor: unsupported,
    devResetRefundCursor: unsupported,
    devResetState: unsupported,
    refundBatch: unsupported,
    devForceRefundToTreasury: unsupported,
    devClearPhase: unsupported,
    debugSnapshot: () => ({
      presaleAddress,
      chainId,
      status,
      presaleState,
      roundId: String(roundId),
      participantCount: Number(participantCount || 0n),
      maxParticipants: String(maxParticipants || 0n),
      contributionWeiPerWallet: String(contributionWeiPerWallet || 0n),
      totalRaisedWei: String(totalRaisedWei || 0n),
      userContributedWei: String(userContributedWei || 0n),
      authStatus,
      authDeadline,
      authSigLen: authSig ? String(authSig).length : 0,
      txHash,
      uiError,
    }),
  };
}
